import React, { useState, useRef, useEffect } from 'react';
import '../scss/components/_addpgmodal.scss';
import {
  insert_pgowner_with_files,
  getAmenitiesList,
  getLocalities,
  getStates,
  getCitiesByStateId,
  getLocalitiesByCityId
} from '../apis/services/pgApi';

// ─── Success Popup Modal ────────────────────────────────────────────────
const SuccessPopup = ({ isOpen, onClose, pgName }) => {
  if (!isOpen) return null;

  return (
    <div className="apm-success-overlay">
      <div className="apm-success-modal">
        <div className="apm-success-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12L11 15L16 9" />
          </svg>
        </div>
        <h3 className="apm-success-title">PG Registered Successfully!</h3>
        <p className="apm-success-message">
          Your PG "{pgName}" has been successfully registered.<br />
          It will be verified by our admin within 12-24 hours.<br />
          Please wait for the verification.
        </p>
        <button className="apm-success-btn" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
};

// ─── Toast Notification Component ───────────────────────────────────────
const Toast = ({ message, type = 'error', onClose, duration = 5000 }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div className={`apm-toast apm-toast-${type}`}>
      <div className="apm-toast-content">
        {type === 'error' && (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        )}
        {type === 'success' && (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        )}
        {type === 'warning' && (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 9v4M12 17h.01" />
            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
          </svg>
        )}
        <span>{message}</span>
      </div>
      <button className="apm-toast-close" onClick={onClose}>×</button>
    </div>
  );
};

// ─── Toast Container ───────────────────────────────────────────────────
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="apm-toast-container">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
          duration={toast.duration}
        />
      ))}
    </div>
  );
};

// ─── File Upload Sub-component ────────────────────────────────────────
const FileInput = ({ label, accept, value, onChange, hint, required = false, error }) => {
  const ref = useRef();
  return (
    <div className="apm-file-wrap">
      <label className="apm-label">
        {label}
        {required && <span className="apm-req">*</span>}
      </label>
      <div className={`apm-file-box ${value ? 'has-file' : ''} ${error ? 'apm-file-err' : ''}`} onClick={() => ref.current.click()}>
        <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
          onChange={e => onChange(e.target.files[0] || null)} />
        {value ? (
          <div className="apm-file-chosen">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span className="apm-file-name">{value.name}</span>
            <button className="apm-file-clear"
              onClick={e => { e.stopPropagation(); onChange(null) }}>×</button>
          </div>
        ) : (
          <div className="apm-file-empty">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <span>{hint || 'Click to upload'}</span>
          </div>
        )}
      </div>
      {error && <span className="apm-err-msg">{error}</span>}
    </div>
  );
};

// ─── Single Image Upload Component ─────────────────────────────────────
const SingleImageUpload = ({ image, onImageChange, onError, maxSizeMB = 5 }) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef();
  const [preview, setPreview] = useState(null);

  // ── Sync preview with image prop ──
  useEffect(() => {
    if (image) {
      const url = URL.createObjectURL(image);
      setPreview(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
      setPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  const validateAndAddImage = (file) => {
    if (!file) return false;

    if (!file.type.startsWith('image/')) {
      onError(`${file.name} is not an image file`, 'error');
      return false;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      onError(`${file.name} exceeds ${maxSizeMB}MB limit`, 'error');
      return false;
    }

    onImageChange(file);
    onError('Image uploaded successfully', 'success');
    return true;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      validateAndAddImage(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      validateAndAddImage(files[0]);
    }
    e.target.value = null;
  };

  const handleRemoveImage = () => {
    onImageChange(null);
    onError('Image removed', 'success');
  };

  return (
    <div className="apm-single-image-upload">
      <div
        className={`apm-dropzone ${dragActive ? 'drag-active' : ''} ${preview ? 'has-image' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !preview && fileInputRef.current.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/jpg,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {preview ? (
          <div className="apm-image-preview-container">
            <img src={preview} alt="PG Photo Preview" className="apm-image-preview" />
            <button
              className="apm-image-remove-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage();
              }}
              title="Remove image"
            >
              ×
            </button>
          </div>
        ) : (
          <>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <p className="apm-dropzone-text">
              Drag & drop image here or click to browse
            </p>
            <p className="apm-dropzone-hint">
              Upload a single image • Up to {maxSizeMB}MB • JPG, PNG, WebP
            </p>
          </>
        )}
      </div>

      {preview && (
        <p className="apm-hint">
          Image uploaded successfully. Click × to remove.
        </p>
      )}
    </div>
  );
};

// ─── Custom Items Component ───────────────────────────────────────
const CustomItemsList = ({ items = [], onAdd, onRemove, onUpdate, placeholder }) => {
  const [newItem, setNewItem] = useState('');

  const handleAdd = () => {
    if (newItem.trim()) {
      onAdd(newItem.trim());
      setNewItem('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAdd();
    }
  };

  return (
    <div className="apm-custom-items">
      {items.length > 0 && (
        <div className="apm-custom-items-list">
          {items.map((item, index) => (
            <div key={index} className="apm-custom-item">
              <input
                type="text"
                className="apm-input apm-input-sm apm-custom-input"
                value={item}
                onChange={(e) => onUpdate(index, e.target.value)}
              />
              <button
                className="apm-custom-remove"
                onClick={() => onRemove(index)}
                title="Remove"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="apm-custom-add">
        <input
          type="text"
          className="apm-input apm-input-sm"
          placeholder={placeholder}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button className="apm-btn-add" onClick={handleAdd}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add
        </button>
      </div>
    </div>
  );
};

// ─── Dynamic Checkbox Group with API Data ─────────────────────────────
const DynamicCheckboxGroup = ({ label, items = [], selectedItems = {}, onChange, customItems = [], onCustomAdd, onCustomRemove, onCustomUpdate, customPlaceholder }) => {
  return (
    <div className="apm-checkbox-group">
      <label className="apm-label">{label}</label>
      <div className="apm-checkbox-grid">
        {items.map(item => (
          <label key={item.CategoryID} className="apm-checkbox">
            <input
              type="checkbox"
              checked={selectedItems[item.CategoryID] || false}
              onChange={e => onChange(item.CategoryID, e.target.checked, item.CategoryName)}
            />
            <span>{item.CategoryName}</span>
          </label>
        ))}
      </div>

      {onCustomAdd && (
        <div className="apm-custom-section">
          <div className="apm-custom-header">
            <span className="apm-custom-label">Custom Items</span>
          </div>
          <CustomItemsList
            items={customItems}
            onAdd={onCustomAdd}
            onRemove={onCustomRemove}
            onUpdate={onCustomUpdate}
            placeholder={customPlaceholder}
          />
        </div>
      )}
    </div>
  );
};

const SHARING_OPTIONS = [
  { key: 'single', label: 'Single' },
  { key: 'double', label: 'Double' },
  { key: 'triple', label: 'Triple' },
  { key: 'quad', label: 'Quad' },
  { key: 'dorm', label: 'Dorm' }
];

// ─── Initial form state ───────────────────────────────────────────
const INITIAL_FORM = {
  pgCode: `PG${Date.now()}`,
  pgName: '',
  ownerName: '',
  mobileNumber: '',
  whatsappNumber: '',
  emailId: '',
  stateId: '',
  stateName: '',
  city: '',
  cityId: '',
  locality: '',
  localityId: '',
  address: '',
  landmark: '',
  pincode: '',
  pgType: '',
  aboutPG: '',
  aboutOwner: '',
  pgRegistrationDate: new Date().toISOString().split('T')[0],
  pgAgreementType: '',
  contactPersonName: '',
  contactPersonNumber: '',

  latitude: '',
  longitude: '',

  ownerAadhaar: null,
  ownerPanCard: null,
  ownershipProof: null,

  pgPhoto: null,

  numberOfFloors: '',
  totalRooms: '',
  pGRent: '',
  electricityUnit: '',

  amenitiesCommon: {},
  commonAmenitiesList: [],
  customCommonAmenities: [],

  amenitiesServices: {},
  servicesList: [],
  customServices: [],

  foodAvailable: false,
  foodType: '',
  mealsPerDay: '',

  sharingTypes: {
    single: false,
    double: false,
    triple: false,
    quad: false,
    dorm: false
  },

  rentingTerms: {
    securityDeposit: '',
    lockInPeriod: '',
    noticePeriod: '',
    electricity: ''
  },

  propertyRules: {
    noDamage: false,
    sharedAmenities: false,
    switchOffAppliances: false,
    maintainCleanliness: false
  },
  customRules: [],

  smokingAlcoholPolicy: '',
  guestPolicy: '',
  entryExitTime: '',
  cctvInstalled: '',
  documentRequired: '',
  pgDiscount: '',
  pgRating: '',
  pgCategory: '',
  pgStatus: 'NEW',
  yearsOfExperience: ''
};

// ─── Validation Functions ─────────────────────────────────────────────
const validateField = (name, value, form) => {
  const isEmpty = (val) => val === undefined || val === null || val.toString().trim() === '';

  switch (name) {
    case 'pgName':
      if (isEmpty(value)) return 'PG Name is required';
      if (value.length < 3) return 'PG Name must be at least 3 characters';
      if (value.length > 100) return 'PG Name must be less than 100 characters';
      return '';

    case 'ownerName':
      if (isEmpty(value)) return 'Owner Name is required';
      if (value.length < 2) return 'Owner Name must be at least 2 characters';
      if (value.length > 100) return 'Owner Name must be less than 100 characters';
      if (!/^[a-zA-Z\s\-\.]+$/.test(value.trim())) return 'Owner Name should only contain letters, spaces, hyphens, and dots';
      return '';

    case 'mobileNumber':
      if (isEmpty(value)) return 'Mobile Number is required';
      if (!/^[0-9]{10}$/.test(value.trim())) return 'Mobile number must be exactly 10 digits';
      return '';

    case 'whatsappNumber':
      if (isEmpty(value)) return 'WhatsApp Number is required';
      if (!/^[0-9]{10}$/.test(value.trim())) return 'WhatsApp number must be exactly 10 digits';
      return '';

    case 'emailId':
      if (isEmpty(value)) return 'Email ID is required';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) return 'Please enter a valid email address (e.g., name@example.com)';
      return '';

    case 'stateId':
      if (isEmpty(value)) return 'Please select a state';
      return '';

    case 'cityId':
      if (isEmpty(value)) return 'Please select a city';
      return '';

    case 'localityId':
      if (isEmpty(value)) return 'Please select a locality';
      return '';

    case 'address':
      if (isEmpty(value)) return 'Address is required';
      if (value.length < 10) return 'Please enter a complete address (minimum 10 characters)';
      if (value.length > 500) return 'Address must be less than 500 characters';
      return '';

    case 'landmark':
      if (isEmpty(value)) return 'Landmark is required';
      if (value.length > 200) return 'Landmark must be less than 200 characters';
      return '';

    case 'pincode':
      if (isEmpty(value)) return 'Pincode is required';
      if (!/^[0-9]{6}$/.test(value.trim())) return 'Pincode must be exactly 6 digits';
      return '';

    case 'pgType':
      if (isEmpty(value)) return 'PG Type is required';
      return '';

    case 'aboutPG':
      if (isEmpty(value)) return 'About PG is required';
      if (value.length > 1000) return 'About PG must be less than 1000 characters';
      return '';

    case 'aboutOwner':
      if (isEmpty(value)) return 'About Owner is required';
      if (value.length > 1000) return 'About Owner must be less than 1000 characters';
      return '';

    case 'pgRegistrationDate':
      if (isEmpty(value)) return 'PG Registration Date is required';
      return '';

    case 'pgAgreementType':
      if (isEmpty(value)) return 'PG Agreement Type is required';
      return '';

    case 'contactPersonName':
      if (isEmpty(value)) return 'Contact Person Name is required';
      if (value.length < 2) return 'Contact Person Name must be at least 2 characters';
      if (value.length > 100) return 'Contact Person Name must be less than 100 characters';
      if (!/^[a-zA-Z\s\-\.]+$/.test(value.trim())) return 'Contact Person Name should only contain letters, spaces, hyphens, and dots';
      return '';

    case 'contactPersonNumber':
      if (isEmpty(value)) return 'Contact Person Number is required';
      if (!/^[0-9]{10}$/.test(value.trim())) return 'Contact number must be exactly 10 digits';
      return '';

    case 'numberOfFloors':
      if (isEmpty(value)) return 'Number of floors is required';
      const floors = parseInt(value);
      if (isNaN(floors) || floors < 1) return 'Number of floors must be at least 1';
      if (floors > 50) return 'Number of floors cannot exceed 50';
      return '';

    case 'totalRooms':
      if (isEmpty(value)) return 'Total rooms is required';
      const rooms = parseInt(value);
      if (isNaN(rooms) || rooms < 1) return 'Total rooms must be at least 1';
      if (rooms > 500) return 'Total rooms cannot exceed 500';
      return '';

    case 'pGRent':
      if (isEmpty(value)) return 'Rent amount is required';
      const rent = parseFloat(value);
      if (isNaN(rent) || rent <= 0) return 'Rent amount must be greater than 0';
      if (rent > 100000) return 'Rent amount cannot exceed ₹1,00,000';
      return '';

    case 'securityDeposit':
      if (isEmpty(value)) return 'Security Deposit is required';
      const deposit = parseFloat(value);
      if (isNaN(deposit) || deposit < 0) return 'Security deposit must be a valid amount';
      return '';

    case 'yearsOfExperience':
      if (isEmpty(value)) return 'Years of Experience is required';
      const years = parseInt(value);
      if (isNaN(years) || years < 0) return 'Years of experience must be a valid number';
      if (years > 100) return 'Years of experience cannot exceed 100';
      return '';

    case 'latitude':
      if (isEmpty(value)) return 'Latitude is required';
      const lat = parseFloat(value);
      if (isNaN(lat) || lat < -90 || lat > 90) return 'Latitude must be between -90 and 90';
      return '';

    case 'longitude':
      if (isEmpty(value)) return 'Longitude is required';
      const lng = parseFloat(value);
      if (isNaN(lng) || lng < -180 || lng > 180) return 'Longitude must be between -180 and 180';
      return '';

    case 'entryExitTime':
      if (isEmpty(value)) return 'Entry/Exit Time is required';
      if (value.length > 100) return 'Entry/Exit Time must be less than 100 characters';
      return '';

    case 'guestPolicy':
      if (isEmpty(value)) return 'Guest Policy is required';
      return '';

    case 'smokingAlcoholPolicy':
      if (isEmpty(value)) return 'Smoking/Alcohol Policy is required';
      return '';

    case 'documentRequired':
      if (isEmpty(value)) return 'Documents Required is required';
      if (value.length > 200) return 'Documents Required must be less than 200 characters';
      return '';

    // ── Renting terms (simple names) ──
    case 'electricity':
      if (isEmpty(value)) return 'Electricity Charges is required';
      if (value.length > 100) return 'Electricity Charges must be less than 100 characters';
      return '';

    case 'lockInPeriod':
      if (isEmpty(value)) return 'Lock-in Period is required';
      const lock = parseInt(value);
      if (isNaN(lock) || lock < 0) return 'Lock-in period must be a valid number (0 or more)';
      return '';

    case 'noticePeriod':
      if (isEmpty(value)) return 'Notice Period is required';
      const notice = parseInt(value);
      if (isNaN(notice) || notice < 0) return 'Notice period must be a valid number (0 or more)';
      return '';

    default:
      return '';
  }
};

// ─── AddPGModal Component ─────────────────────────────────────────
const AddPGModal = ({ isOpen, onClose, onSubmit }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState('basic');
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingLocalities, setLoadingLocalities] = useState(false);

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [submittedPGName, setSubmittedPGName] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchAmenities();
      fetchStates();
    }
  }, [isOpen]);

  // ── Clear sharingTypes error when any sharing is selected ──
  useEffect(() => {
    const hasSharing = Object.values(form.sharingTypes).some(v => v === true);
    if (hasSharing && errors.sharingTypes) {
      setErrors(prev => ({ ...prev, sharingTypes: '' }));
    }
  }, [form.sharingTypes, errors.sharingTypes]);

  if (!isOpen) return null;

  const addToast = (message, type = 'error', duration = 5000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // ── set() with special handling for documents and sharing types ──
  const set = (path, value) => {
    const keys = path.split('.');
    setForm(prev => {
      let newForm = { ...prev };
      let current = newForm;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newForm;
    });

    const fieldName = keys[keys.length - 1];
    if (fieldName) {
      // For document fields, clear error if file is present (or removed)
      if (['ownerAadhaar', 'ownerPanCard', 'ownershipProof'].includes(fieldName)) {
        // Clear the error regardless – we'll rely on validateForm for final check
        setErrors(prev => ({ ...prev, [fieldName]: '' }));
        return;
      }
      // For other fields, validate
      const error = validateField(fieldName, value, form);
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    }
  };

  const reset = () => {
    setForm({
      ...INITIAL_FORM,
      pgCode: `PG${Date.now()}`,
      pgRegistrationDate: new Date().toISOString().split('T')[0]
    });
    setErrors({});
    setActiveSection('basic');
    setToasts([]);
    setStates([]);
    setCities([]);
    setLocalities([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const fetchStates = async () => {
    setLoadingStates(true);
    try {
      const response = await getStates(null);
      if (response && response.length > 0) {
        setStates(response);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      addToast('Failed to load states', 'error');
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchCitiesByState = async (stateId) => {
    setLoadingCities(true);
    try {
      const response = await getCitiesByStateId(stateId);
      if (response && response.length > 0) {
        setCities(response);
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      addToast('Failed to load cities', 'error');
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchLocalitiesByCity = async (cityId) => {
    setLoadingLocalities(true);
    try {
      const response = await getLocalitiesByCityId(cityId);
      if (response && response.length > 0) {
        setLocalities(response);
      } else {
        setLocalities([]);
      }
    } catch (error) {
      console.error('Error fetching localities:', error);
      addToast('Failed to load localities', 'error');
    } finally {
      setLoadingLocalities(false);
    }
  };

  const handleStateChange = (e) => {
    const selectedStateId = e.target.value;
    const selectedState = states.find(state => state.StateId.toString() === selectedStateId);

    if (selectedState) {
      // Update form fields for state, and reset dependent fields without triggering validation
      setForm(prev => ({
        ...prev,
        stateId: selectedStateId,
        stateName: selectedState.StateName,
        city: '',
        cityId: '',
        locality: '',
        localityId: '',
        pincode: '',
      }));
      // Validate only the field the user actually touched (stateId), clear errors on dependents
      const stateError = validateField('stateId', selectedStateId, form);
      setErrors(prev => ({
        ...prev,
        stateId: stateError,
        cityId: '',
        localityId: '',
        pincode: '',
      }));
      setCities([]);
      setLocalities([]);
      fetchCitiesByState(selectedStateId);
    }
  };

  const handleCityChange = (e) => {
    const selectedCityId = e.target.value;
    const selectedCity = cities.find(city => city.CityId.toString() === selectedCityId);

    if (selectedCity) {
      // Update form fields for city, and reset dependent fields without triggering validation
      setForm(prev => ({
        ...prev,
        cityId: selectedCityId,
        city: selectedCity.CityName,
        locality: '',
        localityId: '',
        pincode: '',
      }));
      // Validate only the field the user actually touched (cityId), clear errors on dependents
      const cityError = validateField('cityId', selectedCityId, form);
      setErrors(prev => ({
        ...prev,
        cityId: cityError,
        localityId: '',
        pincode: '',
      }));
      setLocalities([]);
      fetchLocalitiesByCity(selectedCityId);
    }
  };

  const handleLocalityChange = (e) => {
    const selectedLocalityId = e.target.value;
    const selectedLocality = localities.find(loc => loc.LocalityId.toString() === selectedLocalityId);

    if (selectedLocality) {
      setForm(prev => ({
        ...prev,
        localityId: selectedLocalityId,
        locality: selectedLocality.LocalityName,
      }));
      // Validate only the field the user actually touched (localityId)
      const localityError = validateField('localityId', selectedLocalityId, form);
      setErrors(prev => ({
        ...prev,
        localityId: localityError,
      }));
      // Do NOT auto-set pincode – let the user enter it manually
    }
  };

  const fetchAmenities = async () => {
    try {
      const commonResponse = await getAmenitiesList('pg');
      if (commonResponse && commonResponse[0]) {
        setForm(prev => ({
          ...prev,
          commonAmenitiesList: commonResponse[0],
          amenitiesCommon: {}
        }));
      }

      const servicesResponse = await getAmenitiesList('service');
      if (servicesResponse && servicesResponse[0]) {
        setForm(prev => ({
          ...prev,
          servicesList: servicesResponse[0],
          amenitiesServices: {}
        }));
      }
    } catch (error) {
      console.error('Error fetching amenities:', error);
      addToast('Failed to load amenities', 'error');
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      addToast('Geolocation is not supported by your browser', 'error');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        set('latitude', latitude.toString());
        set('longitude', longitude.toString());
        setLocationLoading(false);
        addToast('Location fetched successfully', 'success', 2000);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let msg = 'Failed to get location. ';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            msg += 'Permission denied. Please allow location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            msg += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            msg += 'Request timed out.';
            break;
          default:
            msg += 'Unknown error.';
        }
        addToast(msg, 'error');
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // ─── Validation ──────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};

    const allFields = [
      'pgName', 'ownerName', 'mobileNumber', 'whatsappNumber', 'emailId',
      'stateId', 'cityId', 'localityId', 'address', 'landmark', 'pincode',
      'pgType', 'aboutPG', 'aboutOwner', 'pgRegistrationDate', 'pgAgreementType',
      'contactPersonName', 'contactPersonNumber',
      'numberOfFloors', 'totalRooms', 'pGRent',
      'yearsOfExperience', 'latitude', 'longitude',
      'entryExitTime', 'guestPolicy', 'smokingAlcoholPolicy', 'documentRequired'
    ];

    allFields.forEach(field => {
      const error = validateField(field, form[field], form);
      if (error) newErrors[field] = error;
    });

    // Renting terms (simple names)
    const rentingFields = ['electricity', 'lockInPeriod', 'noticePeriod', 'securityDeposit'];
    rentingFields.forEach(f => {
      const error = validateField(f, form.rentingTerms[f], form);
      if (error) newErrors[f] = error;
    });

    // Sharing types
    const hasSharingType = Object.values(form.sharingTypes).some(v => v === true);
    if (!hasSharingType) {
      newErrors.sharingTypes = 'At least one sharing type must be selected';
    }

    // Required documents
    if (!form.ownerAadhaar) newErrors.ownerAadhaar = 'Owner ID Proof 1 is required';
    if (!form.ownerPanCard) newErrors.ownerPanCard = 'Owner ID Proof 2 is required';
    if (!form.ownershipProof) newErrors.ownershipProof = 'PG Electricity Bill is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    const allFields = [
      'pgName', 'ownerName', 'mobileNumber', 'whatsappNumber', 'emailId',
      'stateId', 'cityId', 'localityId', 'address', 'landmark', 'pincode',
      'pgType', 'aboutPG', 'aboutOwner', 'pgRegistrationDate', 'pgAgreementType',
      'contactPersonName', 'contactPersonNumber',
      'numberOfFloors', 'totalRooms', 'pGRent',
      'yearsOfExperience', 'latitude', 'longitude',
      'entryExitTime', 'guestPolicy', 'smokingAlcoholPolicy', 'documentRequired'
    ];

    for (let field of allFields) {
      if (!form[field] || !form[field].toString().trim()) return false;
      if (errors[field]) return false;
    }

    // Renting terms
    const rentingFields = ['electricity', 'lockInPeriod', 'noticePeriod', 'securityDeposit'];
    for (let f of rentingFields) {
      if (!form.rentingTerms[f] || !form.rentingTerms[f].toString().trim()) return false;
      if (errors[f]) return false;
    }

    const hasSharingType = Object.values(form.sharingTypes).some(v => v === true);
    if (!hasSharingType) return false;

    if (!form.ownerAadhaar) return false;
    if (!form.ownerPanCard) return false;
    if (!form.ownershipProof) return false;

    // Catch any other errors
    for (let key in errors) {
      if (errors[key]) return false;
    }
    return true;
  };

  const buildRulesArray = () => {
    const rules = [];

    if (form.ownerName) {
      rules.push({ Rules: form.ownerName, RulesType: 'OWNER', Sequence: 1, Status: 1 });
    }
    if (form.yearsOfExperience) {
      rules.push({ Rules: `Since ${form.yearsOfExperience} years`, RulesType: 'OWNER', Sequence: 2, Status: 1 });
    }
    if (form.aboutOwner) {
      rules.push({ Rules: form.aboutOwner, RulesType: 'OWNER', Sequence: 3, Status: 1 });
    }
    if (form.aboutPG) {
      rules.push({ Rules: form.aboutPG, RulesType: 'ABOUT US', Sequence: 1, Status: 1 });
    }

    const propertyRuleTexts = {
      noDamage: 'No damage to property. Tenants found causing damage will be asked to leave immediately',
      sharedAmenities: 'Common amenities are shared — do not keep them in personal possession',
      switchOffAppliances: 'Switch off all appliances when not in use',
      maintainCleanliness: 'Maintain cleanliness in all common areas at all times'
    };

    Object.keys(form.propertyRules).forEach((key, index) => {
      if (form.propertyRules[key] && propertyRuleTexts[key]) {
        rules.push({ Rules: propertyRuleTexts[key], RulesType: 'PROPERTY', Sequence: index + 1, Status: 1 });
      }
    });

    if (form.customRules && form.customRules.length > 0) {
      form.customRules.forEach((rule, index) => {
        if (rule && rule.trim()) {
          rules.push({ Rules: rule.trim(), RulesType: 'CUSTOM', Sequence: (rules.length + index + 1), Status: 1 });
        }
      });
    }
    return rules;
  };

  const buildEquipmentCategories = () => {
    const categories = [];
    Object.keys(form.amenitiesCommon).forEach(categoryId => {
      if (form.amenitiesCommon[categoryId]) {
        Object.keys(form.sharingTypes).forEach(roomType => {
          if (form.sharingTypes[roomType]) {
            categories.push({ CategoryID: parseInt(categoryId), RoomType: roomType.toUpperCase() });
          }
        });
      }
    });
    Object.keys(form.amenitiesServices).forEach(categoryId => {
      if (form.amenitiesServices[categoryId]) {
        Object.keys(form.sharingTypes).forEach(roomType => {
          if (form.sharingTypes[roomType]) {
            categories.push({ CategoryID: parseInt(categoryId), RoomType: roomType.toUpperCase() });
          }
        });
      }
    });
    return categories;
  };

  const handleAmenityChange = (type, categoryId, checked, categoryName) => {
    if (type === 'common') {
      set('amenitiesCommon', { ...form.amenitiesCommon, [categoryId]: checked });
    } else if (type === 'service') {
      set('amenitiesServices', { ...form.amenitiesServices, [categoryId]: checked });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      const firstError = Object.values(errors)[0];
      if (firstError) {
        addToast(firstError, 'error');
        const errorFields = Object.keys(errors);
        if (errorFields.some(f => ['pgName','ownerName','mobileNumber','stateId','cityId','localityId','address','landmark','pincode','whatsappNumber','emailId','pgType','aboutPG','aboutOwner','pgAgreementType','contactPersonName','contactPersonNumber','yearsOfExperience','latitude','longitude'].includes(f))) {
          setActiveSection('basic');
        } else if (errorFields.some(f => ['numberOfFloors','totalRooms','pGRent','sharingTypes'].includes(f))) {
          setActiveSection('property');
        } else if (errorFields.some(f => ['entryExitTime','guestPolicy','smokingAlcoholPolicy','documentRequired','securityDeposit','rentingTerms'].includes(f) || f.startsWith('rentingTerms.'))) {
          setActiveSection('terms');
        } else if (errorFields.some(f => ['ownerAadhaar','ownerPanCard','ownershipProof'].includes(f))) {
          setActiveSection('documents');
        }
      }
      return;
    }

    const userId = localStorage.getItem('userId');
    setLoading(true);

    try {
      const jsonData = {
        nUserId: parseInt(userId),
        PGStatus: form.pgStatus,
        PGCode: form.pgCode,
        PGName: form.pgName,
        PGOwnerName: form.ownerName,
        PGPhoneNumber: form.mobileNumber,
        PGWhattsappno: form.whatsappNumber,
        PGEmail: form.emailId,
        PGCity: form.city,
        PGLocality: form.locality,
        PGAddress: form.address,
        PGLandMark: form.landmark,
        PGPinCode: form.pincode,
        PGType: form.pgType,
        PGRegistrationDate: form.pgRegistrationDate,
        PGAgreementType: form.pgAgreementType,
        PGContactPersonName: form.contactPersonName,
        PGContactPersonPhoneNumber: form.contactPersonNumber,
        PGRating: form.pgRating ? parseFloat(form.pgRating) : 0,
        PGCategory: form.pgCategory || 'Standard',
        PGTotalRooms: parseInt(form.totalRooms) || 0,
        FoodIncluded: form.foodAvailable ? 'Yes' : 'No',
        FoodType: form.foodType || 'Veg',
        MealsPerDay: parseInt(form.mealsPerDay) || 0,
        LoundryService: form.amenitiesServices['10'] ? 'Yes' : 'No',
        PowerBackup: form.amenitiesCommon['1'] ? 'Yes' : 'No',
        Securitydeposit: parseFloat(form.rentingTerms.securityDeposit) || 0,
        NoticePeriod: form.rentingTerms.noticePeriod ? `${form.rentingTerms.noticePeriod} Days` : '30 Days',
        EntryExitTime: form.entryExitTime,
        GuestPolicy: form.guestPolicy,
        SmokingAlcohalPolicy: form.smokingAlcoholPolicy,
        CCTVInstalled: form.amenitiesCommon['4'] ? 'Yes' : 'No',
        DocumentRequired: form.documentRequired,
        PGOwnerAAdhar: form.ownerAadhaar ? 'Uploaded' : 'N/A',
        PGOwnerPanNumber: form.ownerPanCard ? 'Uploaded' : 'N/A',
        PGOwnershipProof: form.ownershipProof ? 'Yes' : 'No',
        PGRentalNOC: 'N/A',
        PGEntityProof: 'N/A',
        PGHouseRulesUploaded: 'N/A',
        PGDiscount: parseFloat(form.pgDiscount) || 0,
        PGRent: parseFloat(form.pGRent) || 0,
        ElectricityUnit: form.rentingTerms.electricity || '0 Rs/unit',
        AboutProperty: form.aboutPG,
        LockinPeriod: form.rentingTerms.lockInPeriod ? `${form.rentingTerms.lockInPeriod} Months` : 'No Lock-in',
        PGPhoto: form.pgPhoto ? form.pgPhoto.name : '',
        Latitude: form.latitude || '0',
        Longitude: form.longitude || '0',
        TenantVerified: 'No',
        PGOwnerAAdharDoc: form.ownerAadhaar ? form.ownerAadhaar.name : '',
        PGOwnerPanDoc: form.ownerPanCard ? form.ownerPanCard.name : '',
        FloorCount: parseInt(form.numberOfFloors) || 1,
        SingleRoom: form.sharingTypes.single ? 1 : 0,
        DoubleRoom: form.sharingTypes.double ? 1 : 0,
        TripleRoom: form.sharingTypes.triple ? 1 : 0,
        QuadRoom: form.sharingTypes.quad ? 1 : 0,
        DormRoom: form.sharingTypes.dorm ? 1 : 0,
        PGRules: buildRulesArray(),
        EquipmentCategories: buildEquipmentCategories()
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(jsonData));

      if (form.ownerAadhaar) formData.append('ownerAadhaar', form.ownerAadhaar);
      if (form.ownerPanCard) formData.append('ownerPanCard', form.ownerPanCard);
      if (form.ownershipProof) formData.append('ownershipProof', form.ownershipProof);
      if (form.pgPhoto) formData.append('pgPhoto', form.pgPhoto);

      const response = await insert_pgowner_with_files(formData);

      if (response.data && response.data[0] && response.data[0].PGID) {
        setSubmittedPGName(form.pgName);
        setShowSuccessPopup(true);
        if (onSubmit) {
          onSubmit({
            ...form,
            id: response.data[0].PGID,
            submittedAt: new Date().toISOString()
          });
        }
      } else if (response && response.statusCode === 413) {
        addToast('File size exceeds the limit. Please upload smaller files.', 'error');
      } else {
        addToast('Failed to add PG. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error adding PG:', error);
      addToast(error.response?.data?.message || 'Failed to add PG. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxGroupChange = (group, key, value) => {
    set(group, { ...form[group], [key]: value });
    // Clear sharingTypes error if any sharing type is selected
    if (group === 'sharingTypes' && value && errors.sharingTypes) {
      setErrors(prev => ({ ...prev, sharingTypes: '' }));
    }
  };

  // ─── Custom items handlers ──────────────────────────────────────────
  const handleAddCustomCommonAmenity = (item) => {
    const currentItems = form.customCommonAmenities || [];
    set('customCommonAmenities', [...currentItems, item]);
    addToast(`Added "${item}" to amenities`, 'success', 2000);
  };
  const handleRemoveCustomCommonAmenity = (index) => {
    const currentItems = form.customCommonAmenities || [];
    const removed = currentItems[index];
    set('customCommonAmenities', currentItems.filter((_, i) => i !== index));
    addToast(`Removed "${removed}" from amenities`, 'success', 2000);
  };
  const handleUpdateCustomCommonAmenity = (index, value) => {
    const currentItems = form.customCommonAmenities || [];
    const updated = [...currentItems];
    updated[index] = value;
    set('customCommonAmenities', updated);
  };

  const handleAddCustomService = (item) => {
    const currentItems = form.customServices || [];
    set('customServices', [...currentItems, item]);
    addToast(`Added "${item}" to services`, 'success', 2000);
  };
  const handleRemoveCustomService = (index) => {
    const currentItems = form.customServices || [];
    const removed = currentItems[index];
    set('customServices', currentItems.filter((_, i) => i !== index));
    addToast(`Removed "${removed}" from services`, 'success', 2000);
  };
  const handleUpdateCustomService = (index, value) => {
    const currentItems = form.customServices || [];
    const updated = [...currentItems];
    updated[index] = value;
    set('customServices', updated);
  };

  const handleAddCustomRule = (item) => {
    const currentItems = form.customRules || [];
    set('customRules', [...currentItems, item]);
    addToast(`Added "${item}" to rules`, 'success', 2000);
  };
  const handleRemoveCustomRule = (index) => {
    const currentItems = form.customRules || [];
    const removed = currentItems[index];
    set('customRules', currentItems.filter((_, i) => i !== index));
    addToast(`Removed "${removed}" from rules`, 'success', 2000);
  };
  const handleUpdateCustomRule = (index, value) => {
    const currentItems = form.customRules || [];
    const updated = [...currentItems];
    updated[index] = value;
    set('customRules', updated);
  };

  const sections = [
    { key: 'basic', label: 'Basic Info' },
    { key: 'property', label: 'Property' },
    { key: 'amenities', label: 'Amenities' },
    { key: 'terms', label: 'Terms & Rules' },
    { key: 'images', label: 'PG Photo' },
    { key: 'documents', label: 'Documents' }
  ];

  return (
    <>
      <div className="apm-overlay" >
        <div className="apm-modal" onClick={e => e.stopPropagation()}>

          <div className="apm-head">
            <div>
              <h2 className="apm-title">Add New PG</h2>
              <p className="apm-sub">Fill in the details to add a new paying guest property</p>
            </div>
            <button className="apm-close" onClick={handleClose}>
              <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
                <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="apm-tabs">
            {sections.map(section => (
              <button
                key={section.key}
                className={`apm-tab ${activeSection === section.key ? 'active' : ''}`}
                onClick={() => setActiveSection(section.key)}
              >
                {section.label}
              </button>
            ))}
          </div>

          <div className="apm-form-body">

            {/* ─── Basic Info ─────────────────────────────────────────── */}
            {activeSection === 'basic' && (
              <>
                <div className="apm-section">
                  <h4 className="apm-section-title">PG Information</h4>
                  <div className="apm-grid">
                    <div className={`apm-field apm-field-full ${errors.pgName ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">PG Name <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        placeholder="Enter PG Name"
                        value={form.pgName}
                        onChange={e => set('pgName', e.target.value)}
                        maxLength={100}
                      />
                      {errors.pgName && <span className="apm-err-msg">{errors.pgName}</span>}
                    </div>

                    <div className={`apm-field ${errors.ownerName ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Owner Name <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        placeholder="Enter full name"
                        value={form.ownerName}
                        onChange={e => set('ownerName', e.target.value)}
                        maxLength={100}
                      />
                      {errors.ownerName && <span className="apm-err-msg">{errors.ownerName}</span>}
                    </div>

                    <div className={`apm-field ${errors.mobileNumber ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Mobile Number <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="tel"
                        placeholder="Enter mobile number"
                        value={form.mobileNumber}
                        onChange={e => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          set('mobileNumber', value);
                        }}
                      />
                      {errors.mobileNumber && <span className="apm-err-msg">{errors.mobileNumber}</span>}
                    </div>

                    <div className={`apm-field ${errors.whatsappNumber ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">WhatsApp Number <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="tel"
                        placeholder="Enter WhatsApp number"
                        value={form.whatsappNumber}
                        onChange={e => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          set('whatsappNumber', value);
                        }}
                      />
                      {errors.whatsappNumber && <span className="apm-err-msg">{errors.whatsappNumber}</span>}
                    </div>

                    <div className={`apm-field ${errors.emailId ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Email ID <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="email"
                        placeholder="Enter email ID"
                        value={form.emailId}
                        onChange={e => set('emailId', e.target.value)}
                      />
                      {errors.emailId && <span className="apm-err-msg">{errors.emailId}</span>}
                    </div>

                    <div className={`apm-field ${errors.pgType ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">PG Type <span className="apm-req">*</span></label>
                      <select className="apm-select" value={form.pgType} onChange={e => set('pgType', e.target.value)}>
                        <option value="">Select PG Type</option>
                        <option value="Boys">Boys PG</option>
                        <option value="Girls">Girls PG</option>
                        <option value="Co-ed">Co-ed PG</option>
                      </select>
                      {errors.pgType && <span className="apm-err-msg">{errors.pgType}</span>}
                    </div>

                    <div className={`apm-field ${errors.yearsOfExperience ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Years of Experience <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Enter years of experience as PG owner"
                        value={form.yearsOfExperience}
                        onChange={e => set('yearsOfExperience', e.target.value)}
                      />
                      {errors.yearsOfExperience && <span className="apm-err-msg">{errors.yearsOfExperience}</span>}
                    </div>
                  </div>
                </div>

                <div className="apm-section">
                  <h4 className="apm-section-title">About</h4>
                  <div className="apm-grid">
                    <div className={`apm-field apm-field-full ${errors.aboutPG ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">About PG <span className="apm-req">*</span></label>
                      <textarea className="apm-textarea" rows={3}
                        placeholder="Describe your PG property - location highlights, facilities, special features, etc."
                        value={form.aboutPG} onChange={e => set('aboutPG', e.target.value)} />
                      {errors.aboutPG && <span className="apm-err-msg">{errors.aboutPG}</span>}
                    </div>

                    <div className={`apm-field apm-field-full ${errors.aboutOwner ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">About Owner <span className="apm-req">*</span></label>
                      <textarea className="apm-textarea" rows={3}
                        placeholder="Tell us about yourself - experience, hosting philosophy, etc."
                        value={form.aboutOwner} onChange={e => set('aboutOwner', e.target.value)} />
                      {errors.aboutOwner && <span className="apm-err-msg">{errors.aboutOwner}</span>}
                    </div>
                  </div>
                </div>

                <div className="apm-section">
                  <h4 className="apm-section-title">Location Details</h4>
                  <div className="apm-grid">
                    <div className={`apm-field ${errors.stateId ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">State <span className="apm-req">*</span></label>
                      <select
                        className="apm-select"
                        value={form.stateId}
                        onChange={handleStateChange}
                        disabled={loadingStates}
                      >
                        <option value="">Select State</option>
                        {states.map(state => (
                          <option key={state.StateId} value={state.StateId}>
                            {state.StateName}
                          </option>
                        ))}
                      </select>
                      {loadingStates && <span className="apm-hint">Loading states...</span>}
                      {errors.stateId && <span className="apm-err-msg">{errors.stateId}</span>}
                    </div>

                    <div className={`apm-field ${errors.cityId ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">
                        City <span className="apm-req">*</span>
                        {!form.stateId && <span className="apm-hint">&nbsp;(Please select a state first)</span>}
                      </label>
                      <select
                        className="apm-select"
                        value={form.cityId}
                        onChange={handleCityChange}
                        disabled={loadingCities || !form.stateId}
                        style={{ opacity: (!form.stateId) ? 0.6 : 1, cursor: (!form.stateId) ? 'not-allowed' : 'default' }}
                      >
                        <option value="">{!form.stateId ? 'Select a state first' : 'Select City'}</option>
                        {cities.map(city => (
                          <option key={city.CityId} value={city.CityId}>
                            {city.CityName}
                          </option>
                        ))}
                      </select>
                      {loadingCities && <span className="apm-hint">Loading cities...</span>}
                      {errors.cityId && <span className="apm-err-msg">{errors.cityId}</span>}
                    </div>

                    <div className={`apm-field ${errors.localityId ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">
                        Locality <span className="apm-req">*</span>
                        {!form.cityId && <span className="apm-hint">&nbsp;(Please select a city first)</span>}
                      </label>
                      <select
                        className="apm-select"
                        value={form.localityId}
                        onChange={handleLocalityChange}
                        disabled={loadingLocalities || !form.cityId}
                        style={{ opacity: (!form.cityId) ? 0.6 : 1, cursor: (!form.cityId) ? 'not-allowed' : 'default' }}
                      >
                        <option value="">{!form.cityId ? 'Select a city first' : 'Select Locality'}</option>
                        {localities.map(loc => (
                          <option key={loc.LocalityId} value={loc.LocalityId}>
                            {loc.LocalityName}
                          </option>
                        ))}
                      </select>
                      {loadingLocalities && <span className="apm-hint">Loading localities...</span>}
                      {errors.localityId && <span className="apm-err-msg">{errors.localityId}</span>}
                    </div>

                    <div className={`apm-field apm-field-full ${errors.address ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Address <span className="apm-req">*</span></label>
                      <textarea
                        className="apm-textarea"
                        rows={2}
                        maxLength={500}
                        placeholder="Enter full address of the PG property"
                        value={form.address}
                        onChange={e => set('address', e.target.value)}
                      />
                      {errors.address && <span className="apm-err-msg">{errors.address}</span>}
                    </div>

                    <div className={`apm-field ${errors.landmark ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Landmark <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        placeholder="Enter landmark"
                        value={form.landmark}
                        onChange={e => set('landmark', e.target.value)}
                        maxLength={200}
                      />
                      {errors.landmark && <span className="apm-err-msg">{errors.landmark}</span>}
                    </div>

                    <div className={`apm-field ${errors.pincode ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Pincode <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="text"
                        placeholder="Enter pincode"
                        value={form.pincode}
                        onChange={e => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                          set('pincode', value);
                        }}
                      />
                      {errors.pincode && <span className="apm-err-msg">{errors.pincode}</span>}
                    </div>
                  </div>
                </div>

                <div className="apm-section">
                  <h4 className="apm-section-title">Contact Person</h4>
                  <div className="apm-grid">
                    <div className={`apm-field ${errors.contactPersonName ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Contact Person Name <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        placeholder="Enter contact person name"
                        value={form.contactPersonName}
                        onChange={e => set('contactPersonName', e.target.value)}
                        maxLength={100}
                      />
                      {errors.contactPersonName && <span className="apm-err-msg">{errors.contactPersonName}</span>}
                    </div>

                    <div className={`apm-field ${errors.contactPersonNumber ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Contact Person Number <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="tel"
                        placeholder="Enter contact person number"
                        value={form.contactPersonNumber}
                        onChange={e => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          set('contactPersonNumber', value);
                        }}
                      />
                      {errors.contactPersonNumber && <span className="apm-err-msg">{errors.contactPersonNumber}</span>}
                    </div>
                  </div>
                </div>

                <div className="apm-section">
                  <h4 className="apm-section-title">Registration Details</h4>
                  <div className="apm-grid">
                    <div className="apm-field">
                      <label className="apm-label">PG Registration Date <span className="apm-req">*</span></label>
                      <input className="apm-input" type="date"
                        value={form.pgRegistrationDate} onChange={e => set('pgRegistrationDate', e.target.value)} />
                      {errors.pgRegistrationDate && <span className="apm-err-msg">{errors.pgRegistrationDate}</span>}
                    </div>

                    <div className={`apm-field ${errors.pgAgreementType ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">PG Agreement Type <span className="apm-req">*</span></label>
                      <select className="apm-select" value={form.pgAgreementType} onChange={e => set('pgAgreementType', e.target.value)}>
                        <option value="">Select type</option>
                        <option value="rental">Rental Agreement</option>
                        <option value="lease">Lease Agreement</option>
                        <option value="license">Owned Property</option>
                      </select>
                      {errors.pgAgreementType && <span className="apm-err-msg">{errors.pgAgreementType}</span>}
                    </div>
                  </div>
                </div>

                <div className="apm-section">
                  <h4 className="apm-section-title">Coordinates</h4>

                  <div className="apm-grid-3">
                    <div className="apm-field">
                      <label className="apm-label">Latitude <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="text"
                        placeholder="Enter latitude (e.g., 19.0760)"
                        value={form.latitude}
                        onChange={e => set('latitude', e.target.value)}
                      />
                      {errors.latitude && <span className="apm-err-msg">{errors.latitude}</span>}
                    </div>

                    <div className="apm-field">
                      <label className="apm-label">Longitude <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="text"
                        placeholder="Enter longitude (e.g., 72.8777)"
                        value={form.longitude}
                        onChange={e => set('longitude', e.target.value)}
                      />
                      {errors.longitude && <span className="apm-err-msg">{errors.longitude}</span>}
                    </div>

                    <div className="apm-field apm-field-button">
                      <button
                        className="apm-btn apm-btn-outline"
                        onClick={getCurrentLocation}
                        disabled={locationLoading}
                        type="button"
                        style={{ width: '100%' }}
                      >
                        {locationLoading ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="apm-spinner" style={{ marginRight: '0.5rem' }}>
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 2a10 10 0 0 1 10 10" />
                            </svg>
                            Fetching...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.5rem' }}>
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                              <circle cx="12" cy="10" r="3" />
                            </svg>
                            Get Location
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ─── Property Section ───────────────────────────────────── */}
            {activeSection === 'property' && (
              <>
                <div className="apm-section">
                  <h4 className="apm-section-title">Property Details</h4>
                  <div className="apm-grid">
                    <div className={`apm-field ${errors.numberOfFloors ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Number of Floors <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="number"
                        min="1"
                        max="50"
                        placeholder="Enter number of floors (e.g., 3)"
                        value={form.numberOfFloors}
                        onChange={e => set('numberOfFloors', e.target.value)}
                      />
                      {errors.numberOfFloors && <span className="apm-err-msg">{errors.numberOfFloors}</span>}
                    </div>

                    <div className={`apm-field ${errors.totalRooms ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Total Rooms <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="number"
                        min="1"
                        max="500"
                        placeholder="Enter total rooms (e.g., 15)"
                        value={form.totalRooms}
                        onChange={e => set('totalRooms', e.target.value)}
                      />
                      {errors.totalRooms && <span className="apm-err-msg">{errors.totalRooms}</span>}
                    </div>

                    <div className={`apm-field ${errors.pGRent ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Rent Amount (₹/month) <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="number"
                        min="0"
                        max="100000"
                        placeholder="Enter rent amount (e.g., 8000)"
                        value={form.pGRent}
                        onChange={e => set('pGRent', e.target.value)}
                      />
                      {errors.pGRent && <span className="apm-err-msg">{errors.pGRent}</span>}
                    </div>
                  </div>
                </div>

                <div className="apm-section">
                  <h4 className="apm-section-title">Sharing Types <span className="apm-req">*</span></h4>
                  <div className="apm-checkbox-group">
                    <label className="apm-label">Select available sharing types (at least one)</label>
                    <div className="apm-checkbox-grid">
                      {SHARING_OPTIONS.map(opt => (
                        <label key={opt.key} className="apm-checkbox">
                          <input
                            type="checkbox"
                            checked={form.sharingTypes[opt.key] || false}
                            onChange={e => handleCheckboxGroupChange('sharingTypes', opt.key, e.target.checked)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {errors.sharingTypes && <span className="apm-err-msg">{errors.sharingTypes}</span>}
                </div>
              </>
            )}

            {/* ─── Amenities Section ──────────────────────────────────── */}
            {activeSection === 'amenities' && (
              <>
                <div className="apm-section">
                  <h4 className="apm-section-title">Common Amenities</h4>
                  <DynamicCheckboxGroup
                    label="Select common amenities"
                    items={form.commonAmenitiesList}
                    selectedItems={form.amenitiesCommon}
                    onChange={(id, checked, name) => handleAmenityChange('common', id, checked, name)}
                    customItems={form.customCommonAmenities || []}
                    onCustomAdd={handleAddCustomCommonAmenity}
                    onCustomRemove={handleRemoveCustomCommonAmenity}
                    onCustomUpdate={handleUpdateCustomCommonAmenity}
                    customPlaceholder="Add custom amenity (e.g., Gym, Swimming Pool)"
                  />
                </div>

                <div className="apm-section">
                  <h4 className="apm-section-title">Food</h4>
                  <div className="apm-checkbox-group">
                    <label className="apm-checkbox apm-checkbox-large">
                      <input
                        type="checkbox"
                        checked={form.foodAvailable}
                        onChange={e => set('foodAvailable', e.target.checked)}
                      />
                      <span>Food available at PG</span>
                    </label>
                  </div>

                  {form.foodAvailable && (
                    <div className="apm-grid" style={{ marginTop: '1rem' }}>
                      <div className="apm-field">
                        <label className="apm-label">Food Type</label>
                        <select className="apm-select" value={form.foodType} onChange={e => set('foodType', e.target.value)}>
                          <option value="">Select type</option>
                          <option value="Veg">Vegetarian</option>
                          <option value="Non-Veg">Non-Vegetarian</option>
                          <option value="Both">Both</option>
                        </select>
                      </div>

                      <div className="apm-field">
                        <label className="apm-label">Meals Per Day</label>
                        <input
                          className="apm-input"
                          type="number"
                          min="0"
                          max="4"
                          placeholder="Enter number of meals per day (e.g., 3)"
                          value={form.mealsPerDay}
                          onChange={e => set('mealsPerDay', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ─── Terms & Rules ──────────────────────────────────────── */}
            {activeSection === 'terms' && (
              <>
                <div className="apm-section">
                  <h4 className="apm-section-title">Renting Terms</h4>
                  <div className="apm-grid">
                    <div className={`apm-field ${errors.securityDeposit ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Security Deposit (₹) <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="number"
                        min="0"
                        placeholder="Enter security deposit amount (e.g., 10000)"
                        value={form.rentingTerms.securityDeposit}
                        onChange={e => set('rentingTerms.securityDeposit', e.target.value)}
                      />
                      {errors.securityDeposit && <span className="apm-err-msg">{errors.securityDeposit}</span>}
                    </div>

                    <div className={`apm-field ${errors.lockInPeriod ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Lock‑in Period (months) <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="number"
                        min="0"
                        placeholder="Enter lock-in period in months (e.g., 6)"
                        value={form.rentingTerms.lockInPeriod}
                        onChange={e => set('rentingTerms.lockInPeriod', e.target.value)}
                      />
                      {errors.lockInPeriod && <span className="apm-err-msg">{errors.lockInPeriod}</span>}
                    </div>

                    <div className={`apm-field ${errors.noticePeriod ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Notice Period (days) <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        type="number"
                        min="0"
                        placeholder="Enter notice period in days (e.g., 30)"
                        value={form.rentingTerms.noticePeriod}
                        onChange={e => set('rentingTerms.noticePeriod', e.target.value)}
                      />
                      {errors.noticePeriod && <span className="apm-err-msg">{errors.noticePeriod}</span>}
                    </div>

                    <div className={`apm-field ${errors.electricity ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Electricity Charges <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        placeholder="Enter electricity charges (e.g., As per meter / ₹8 per unit)"
                        value={form.rentingTerms.electricity}
                        onChange={e => set('rentingTerms.electricity', e.target.value)}
                        maxLength={100}
                      />
                      {errors.electricity && <span className="apm-err-msg">{errors.electricity}</span>}
                    </div>
                  </div>
                </div>

                <div className="apm-section">
                  <h4 className="apm-section-title">Property Policies</h4>
                  <div className="apm-grid">
                    <div className={`apm-field ${errors.entryExitTime ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Entry/Exit Time <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        placeholder="Enter entry/exit time (e.g., 24 Hours, 6 AM - 11 PM)"
                        value={form.entryExitTime}
                        onChange={e => set('entryExitTime', e.target.value)}
                        maxLength={100}
                      />
                      {errors.entryExitTime && <span className="apm-err-msg">{errors.entryExitTime}</span>}
                    </div>

                    <div className={`apm-field ${errors.guestPolicy ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Guest Policy <span className="apm-req">*</span></label>
                      <select
                        className="apm-select"
                        value={form.guestPolicy}
                        onChange={e => set('guestPolicy', e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="Allowed">Allowed</option>
                        <option value="Not Allowed">Not Allowed</option>
                        <option value="With Prior Notice">With Prior Notice</option>
                      </select>
                      {errors.guestPolicy && <span className="apm-err-msg">{errors.guestPolicy}</span>}
                    </div>

                    <div className={`apm-field ${errors.smokingAlcoholPolicy ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Smoking/Alcohol Policy <span className="apm-req">*</span></label>
                      <select
                        className="apm-select"
                        value={form.smokingAlcoholPolicy}
                        onChange={e => set('smokingAlcoholPolicy', e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="Not Allowed">Not Allowed</option>
                        <option value="Allowed">Allowed</option>
                        <option value="Designated Areas">Designated Areas</option>
                      </select>
                      {errors.smokingAlcoholPolicy && <span className="apm-err-msg">{errors.smokingAlcoholPolicy}</span>}
                    </div>

                    <div className={`apm-field ${errors.documentRequired ? 'apm-field-err' : ''}`}>
                      <label className="apm-label">Documents Required <span className="apm-req">*</span></label>
                      <input
                        className="apm-input"
                        placeholder="Enter documents required (e.g., Aadhar Card, PAN Card)"
                        value={form.documentRequired}
                        onChange={e => set('documentRequired', e.target.value)}
                        maxLength={200}
                      />
                      {errors.documentRequired && <span className="apm-err-msg">{errors.documentRequired}</span>}
                    </div>
                  </div>
                </div>

                <div className="apm-section">
                  <h4 className="apm-section-title">Property Rules</h4>
                  <div className="apm-checkbox-group">
                    <label className="apm-checkbox apm-checkbox-large">
                      <input
                        type="checkbox"
                        checked={form.propertyRules.noDamage}
                        onChange={e => set('propertyRules.noDamage', e.target.checked)}
                      />
                      <span>No damage to property. Tenants found causing damage will be asked to leave immediately</span>
                    </label>

                    <label className="apm-checkbox apm-checkbox-large">
                      <input
                        type="checkbox"
                        checked={form.propertyRules.sharedAmenities}
                        onChange={e => set('propertyRules.sharedAmenities', e.target.checked)}
                      />
                      <span>Common amenities are shared — do not keep them in personal possession</span>
                    </label>

                    <label className="apm-checkbox apm-checkbox-large">
                      <input
                        type="checkbox"
                        checked={form.propertyRules.switchOffAppliances}
                        onChange={e => set('propertyRules.switchOffAppliances', e.target.checked)}
                      />
                      <span>Switch off all appliances when not in use</span>
                    </label>

                    <label className="apm-checkbox apm-checkbox-large">
                      <input
                        type="checkbox"
                        checked={form.propertyRules.maintainCleanliness}
                        onChange={e => set('propertyRules.maintainCleanliness', e.target.checked)}
                      />
                      <span>Maintain cleanliness in all common areas at all times</span>
                    </label>
                  </div>

                  <div className="apm-custom-section">
                    <div className="apm-custom-header">
                      <span className="apm-custom-label">Custom Rules</span>
                    </div>
                    <CustomItemsList
                      items={form.customRules || []}
                      onAdd={handleAddCustomRule}
                      onRemove={handleRemoveCustomRule}
                      onUpdate={handleUpdateCustomRule}
                      placeholder="Add custom rule"
                    />
                  </div>
                </div>
              </>
            )}

            {/* ─── Images Section ────────────────────────────────────── */}
            {activeSection === 'images' && (
              <div className="apm-section">
                <h4 className="apm-section-title">PG Photo</h4>
                <SingleImageUpload
                  image={form.pgPhoto}
                  onImageChange={(file) => set('pgPhoto', file)}
                  onError={addToast}
                  maxSizeMB={5}
                />
                <p className="apm-hint">
                  Upload a clear, high-quality image of your PG property. This will be displayed as the main photo.
                </p>
              </div>
            )}

            {/* ─── Documents Section ──────────────────────────────────── */}
            {activeSection === 'documents' && (
              <div className="apm-section">
                <h4 className="apm-section-title">Documents</h4>
                <div className="apm-grid apm-grid-2">
                  <FileInput
                    label="Owner ID Proof 1"
                    accept=".pdf,.jpg,.jpeg,.png"
                    value={form.ownerAadhaar}
                    onChange={v => set('ownerAadhaar', v)}
                    hint="PDF or image"
                    required
                    error={errors.ownerAadhaar}
                  />
                  <FileInput
                    label="Owner ID Proof 2"
                    accept=".pdf,.jpg,.jpeg,.png"
                    value={form.ownerPanCard}
                    onChange={v => set('ownerPanCard', v)}
                    hint="PDF or image"
                    required
                    error={errors.ownerPanCard}
                  />
                  <FileInput
                    label="PG Electricity Bill"
                    accept=".pdf,.jpg,.jpeg,.png"
                    value={form.ownershipProof}
                    onChange={v => set('ownershipProof', v)}
                    hint="PDF or image"
                    required
                    error={errors.ownershipProof}
                  />
                </div>
                <p className="apm-hint">Upload clear, legible documents for verification</p>
              </div>
            )}

          </div>

          {/* ─── Footer ────────────────────────────────────────────────── */}
          <div className="apm-footer">
            <button className="apm-btn apm-btn-outline" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button
              className="apm-btn apm-btn-primary"
              onClick={handleSubmit}
              disabled={loading || !isFormValid()}
              title={!isFormValid() ? 'Please fill all required fields correctly' : ''}
            >
              {loading ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="apm-spinner">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Add PG
                </>
              )}
            </button>
          </div>

        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <SuccessPopup
        isOpen={showSuccessPopup}
        onClose={() => {
          setShowSuccessPopup(false);
          reset();
          onClose();
        }}
        pgName={submittedPGName}
      />
    </>
  );
};

export default AddPGModal;